import { inject } from 'vue';
import { useContext } from '@/compat';
import useToast from '@/helpers/misc/dialog/useToast';
import { buildContentFileName, MAX_FILE_NAME_TITLE_LENGTH } from './buildContentFileName';

export const CONTENT_TITLE_GUARD_KEY = 'contentTitleGuard';
export const EXEMPT_CONTENT_TYPES = ['newspaper'];
export const MIN_TITLE_LENGTH = 3;
export { MAX_FILE_NAME_TITLE_LENGTH };

const getExtension = fileName => {
  if (!fileName || typeof fileName !== 'string') {
    return '';
  }
  const lastDot = fileName.lastIndexOf('.');

  return lastDot >= 0 ? fileName.slice(lastDot) : '';
};

const useContentTitleGuard = () => {
  const { $i18n, $lang } = useContext();
  const toast = useToast();
  const context = inject(CONTENT_TITLE_GUARD_KEY, null);

  const isExempt = () => {
    if (!context) {
      return true;
    }

    return EXEMPT_CONTENT_TYPES.includes(context.contentType);
  };

  const getTitle = () => {
    if (!context) {
      return '';
    }
    const raw = context.titleRef?.value;

    return typeof raw === 'string' ? raw.trim() : '';
  };

  const assertTitle = () => {
    if (isExempt()) {
      return;
    }

    if (getTitle().length < MIN_TITLE_LENGTH) {
      toast.warning($i18n.t($lang.content.TITLE_REQUIRED_BEFORE_UPLOAD));
    }
  };

  /**
   * Yuklenecek dosyayi icerik basligina gore yeniden adlandirir.
   *
   * @param {File} file Yuklenecek dosya
   * @param {string|number} [suffix] Basliktan sonra eklenecek ek, ornegin 'adim-2-1'
   * @returns {File} Yeniden adlandirilmis dosya, adlandirilamiyorsa dosyanin kendisi
   */
  const renameForUpload = (file, suffix = '') => {
    if (isExempt() || !(file instanceof File)) {
      return file;
    }
    const title = getTitle();

    if (title.length < MIN_TITLE_LENGTH) {
      return file;
    }
    const newName = buildContentFileName({
      title,
      suffix,
      extension: getExtension(file.name),
      locale: $i18n.locale,
    });

    if (!newName) {
      return file;
    }

    try {
      return new File([file], newName, {
        type: file.type,
        lastModified: file.lastModified,
      });
    } catch {
      return file;
    }
  };

  const guardClick = handler => (...args) => {
    assertTitle();

    return handler ? handler(...args) : undefined;
  };

  return {
    assertTitle,
    renameForUpload,
    isExempt,
    guardClick,
  };
};

export default useContentTitleGuard;
